import { inject, Injectable } from '@angular/core';
import { CONFIG_TOKEN, HealthRecordViewModel } from '@pxl/models/common';
import { HealthRecordService, LoginService, VeterinarianService } from '@pxl/models/contracts';
import { HealthRecordModel, HealthRecordService as HealthRecordBackendService, VeterinarianModel } from '@pxl/shared/generated';
import { LanguageService, RESOURCES } from '@pxl/shared/translation';
import { Convert, getFormattedShortDate, getFormattedShortTime } from '@pxl/shared/util';
import moment from 'moment-timezone';
import { firstValueFrom } from 'rxjs';

@Injectable()
export class DefaultHealthRecordService implements HealthRecordService {
    private readonly healthRecordBackendService = inject(HealthRecordBackendService);
    private readonly loginService = inject(LoginService);
    private readonly veterinariansService = inject(VeterinarianService);
    private readonly languageService = inject(LanguageService);
    private readonly config = inject(CONFIG_TOKEN);

    public async getHealthRecords(patientId: string): Promise<HealthRecordViewModel[]> {
        const healthRecords: HealthRecordModel[] = await firstValueFrom(
            this.healthRecordBackendService.getByPatientId(patientId, this.config.API_VERSION, this.loginService.getToken()),
        );
        const veterinariansMap: Map<string, VeterinarianModel> = await this.veterinariansService.getVeterinariansMap(
            healthRecords,
            (healthRecord) => healthRecord.veterinarianId,
        );

        return healthRecords.map(
            (healthRecord) =>
                new HealthRecordViewModel(
                    this.getDateLocal(healthRecord) ?? '',
                    healthRecord,
                    veterinariansMap.get(healthRecord.veterinarianId),
                ),
        );
    }

    private getDateLocal(healthRecordModel: HealthRecordModel): string | null {
        const dateLocal = Convert.convertUTCString2LocalDate(moment(healthRecordModel.lastDateTimeLocal)?.toISOString());

        if (dateLocal) {
            const date: string = getFormattedShortDate(dateLocal);
            const time: string = getFormattedShortTime(dateLocal);

            const formattedDateTime: string = this.languageService.translate(RESOURCES.petsxl.ui.common.first_and_second_value_separated, [
                { name: 'FirstValue', text: date },
                { name: 'SecondValue', text: time },
            ]);

            return formattedDateTime;
        }

        return null;
    }
}
